import { ChevronDown, ChevronUp, Loader2, Pencil, RefreshCw, Trash2, TriangleAlert } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import type { WidgetSpec } from "../models/Widget";
import type { WidgetData } from "../models/WidgetData";
import KpiWidget from "./widgets/KpiWidget";
import TableWidget from "./widgets/TableWidget";
import ChartWidget from "./widgets/ChartWidget";

const ROW_HEIGHT_PX = 150;

export interface WidgetCardProps {
  widget: WidgetSpec;
  data?: WidgetData;
  refreshing: boolean;
  editing: boolean;
  onRefresh: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function WidgetBody({ widget, data }: { widget: WidgetSpec; data?: WidgetData }) {
  if (!data) {
    return <div className="h-full min-h-16 animate-pulse rounded-xl bg-black/5" />;
  }

  if (data.error) {
    return (
      <div className="flex h-full items-start gap-2 text-amber-600">
        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
        <p className="text-xs font-medium leading-relaxed">{data.error}</p>
      </div>
    );
  }

  const result = data.data;
  if (!result) {
    return <p className="text-sm text-foreground/40 font-medium">Sin datos.</p>;
  }

  switch (result.kind) {
    case "kpi":
      return <KpiWidget value={result.value} />;
    case "table":
      return <TableWidget columns={result.columns ?? []} rows={result.rows ?? []} />;
    case "series":
      return <ChartWidget type={widget.type} points={result.points ?? []} />;
    default:
      return <p className="text-sm text-foreground/40 font-medium">Sin datos.</p>;
  }
}

export function WidgetCard({
  widget,
  data,
  refreshing,
  editing,
  onRefresh,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: WidgetCardProps) {
  const actionButton =
    "size-7 cursor-pointer rounded-lg border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-white hover:text-accent hover:shadow-sm transition-all";

  return (
    <GlassContainer
      className="group relative flex flex-col p-5 overflow-hidden transition-all hover:bg-glass-shine"
    >
      <div className="mb-3 flex items-center gap-2">
        <h3
          className="min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-widest text-foreground/50"
          title={widget.title}
        >
          {widget.title}
        </h3>
        <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {editing && (
            <>
              <button onClick={onMoveUp} className={actionButton} title="Mover antes">
                <ChevronUp className="size-3.5" />
              </button>
              <button onClick={onMoveDown} className={actionButton} title="Mover después">
                <ChevronDown className="size-3.5" />
              </button>
              <button onClick={onEdit} className={actionButton} title="Editar widget">
                <Pencil className="size-3.5" />
              </button>
              <button
                onClick={onDelete}
                className="size-7 cursor-pointer rounded-lg border border-black/5 bg-white/50 flex items-center justify-center text-foreground/50 hover:bg-rose-500 hover:border-rose-500 hover:text-white hover:shadow-sm transition-all"
                title="Quitar widget"
              >
                <Trash2 className="size-3.5" />
              </button>
            </>
          )}
          <button onClick={onRefresh} disabled={refreshing} className={actionButton} title="Actualizar">
            {refreshing ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <WidgetBody widget={widget} data={data} />
      </div>
    </GlassContainer>
  );
}

/** Estilo inline del ítem del grid: span de columnas/filas según el layout del widget. */
export function widgetGridStyle(widget: WidgetSpec): React.CSSProperties {
  const w = Math.min(Math.max(widget.layout.w, 1), 4);
  const h = Math.min(Math.max(widget.layout.h, 1), 3);
  return {
    gridColumn: `span ${w} / span ${w}`,
    gridRow: `span ${h} / span ${h}`,
    minHeight: h * ROW_HEIGHT_PX,
  };
}
